import React, { useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { doc, setDoc, collection } from 'firebase/firestore';

import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Link from '@mui/material/Link';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import { createTheme, ThemeProvider } from '@mui/material/styles';

import { FirebaseContext } from '../../firebase/FirebaseContext';
import { Nav } from '../Nav';
import { Copyright } from './Copyright';

const theme = createTheme();

export const SignUp = () => {
  const navigate = useNavigate();
  const { auth, db } = useContext(FirebaseContext);

  const handleSignUp = useCallback(
    async (e: any) => {
      e.preventDefault();
      e.stopPropagation();
      try {
        const data = new FormData(e.currentTarget);
        const email = data.get('email');
        const password = data.get('password');
        const firstName = data.get('firstName');
        const lastName = data.get('lastName');
        if (
          !email ||
          !password ||
          typeof email !== 'string' ||
          typeof password !== 'string' ||
          typeof firstName !== 'string' ||
          typeof lastName !== 'string'
        ) {
          return;
        }

        const credentials = await createUserWithEmailAndPassword(
          auth,
          email,
          password
        );

        await updateProfile(credentials.user, {
          displayName: `${firstName} ${lastName}`,
        });

        // Add user doc, TODO: other data for user
        await setDoc(doc(db, 'users', credentials.user.uid), {
          firstName,
          lastName,
        });

        // Initialize properties collection
        const propertiesRef = collection(
          db,
          'users',
          credentials.user.uid,
          'properties'
        );

        // Default collection with empty waitlist
        await setDoc(doc(propertiesRef, 'default'), {
          // TOOD: property data
          id: 'default',
          waitlist: [],
        });

        navigate(`/dashboard`);
      } catch (e) {
        // TODO: error handling
        console.log(e);
      }
    },
    [auth]
  );

  return (
    <ThemeProvider theme={theme}>
      <Nav>
        <Container component="main" maxWidth="xs">
          <CssBaseline />
          <Box
            sx={{
              marginTop: 8,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              paddingTop: '10em',
            }}
          >
            <Avatar sx={{ m: 1, bgcolor: 'secondary.main' }}>
              <LockOutlinedIcon />
            </Avatar>
            <Typography component="h1" variant="h5">
              Sign up
            </Typography>
            <Box
              component="form"
              noValidate
              onSubmit={handleSignUp}
              sx={{ mt: 3 }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    autoComplete="given-name"
                    name="firstName"
                    required
                    fullWidth
                    id="firstName"
                    label="First Name"
                    autoFocus
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    fullWidth
                    id="lastName"
                    label="Last Name"
                    name="lastName"
                    autoComplete="family-name"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    id="email"
                    label="Email Address"
                    name="email"
                    autoComplete="email"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    name="password"
                    label="Password"
                    type="password"
                    id="password"
                    autoComplete="new-password"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox value="allowExtraEmails" color="primary" />
                    }
                    label="I want to receive inspiration, marketing promotions and updates via email."
                  />
                </Grid>
              </Grid>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
              >
                Sign Up
              </Button>
              <Grid container justifyContent="flex-end">
                <Grid item>
                  <Link href="#" variant="body2">
                    Already have an account? Sign in
                  </Link>
                </Grid>
              </Grid>
            </Box>
          </Box>
          <Copyright sx={{ mt: 5 }} />
        </Container>
      </Nav>
    </ThemeProvider>
  );
};
